import React from "react";
import clsx from "clsx";
import { Dialog } from "@/components/Dialog";
import { UserRowModel, WebAppPrivileges } from "./SettingsUsersTable";
import { customToast } from "@/utils/customToast";
import { SvgIconType } from "@/svg";
import { useUpdateUserPrivileges } from "@/hooks/useUpdateUserPrivileges";
import { CheckboxOption } from "@/components/CheckboxOption";

type PrivilegeOption = {
  id: keyof WebAppPrivileges;
  label: string;
  icon: SvgIconType;
};

type EditPrivilegesDialogProps = {
  show: boolean;
  onCancel: () => void;
  user: UserRowModel;
  onPrivilegesChangeSuccess?: () => void;
};

export function EditPrivilegesDialog({
  show,
  onCancel,
  user,
  onPrivilegesChangeSuccess,
}: EditPrivilegesDialogProps) {
  const initialPrivileges: WebAppPrivileges = user.web_app_privileges || {
    spotter: false,
    drill_down: false,
    search_data: false,
  };

  const [privileges, setPrivileges] = React.useState<WebAppPrivileges>({ ...initialPrivileges });
  const updatePrivileges = useUpdateUserPrivileges();

  const isPrivilegesChanged =
    privileges.spotter !== initialPrivileges.spotter ||
    privileges.drill_down !== initialPrivileges.drill_down ||
    privileges.search_data !== initialPrivileges.search_data;

  const privilegeOptions: PrivilegeOption[] = [
    {
      id: "drill_down",
      label: "Drill Down",
      icon: "Server",
    },
    {
      id: "search_data",
      label: "Search Data",
      icon: "Server",
    },
    {
      id: "spotter",
      label: "Spotter",
      icon: "Server",
    },
  ];

  const handleTogglePrivilege = (privilegeId: keyof WebAppPrivileges) => {
    setPrivileges((prev) => ({
      ...prev,
      [privilegeId]: !prev[privilegeId],
    }));
  };

  const handleSubmit = async () => {
    try {
      await updatePrivileges.mutateAsync({
        userId: user.id,
        privileges: privileges,
      });

      customToast("User Privileges updated successfully", "success", "bottom");

      if (onPrivilegesChangeSuccess) {
        onPrivilegesChangeSuccess();
      } else {
        onCancel();
      }
    } catch (error) {
      console.error("Failed to update user privileges:", error);
      customToast("Failed to update user privileges", "error", "bottom");
    }
  };

  return (
    <Dialog
      show={show}
      message={
        <div className="p-2">
          <div className="mb-2 text-xs text-dark">Choose Privileges</div>
          <div className="space-y-2 mb-6">
            {privilegeOptions.map((option) => (
              <CheckboxOption
                key={option.id}
                label={option.label}
                icon={option.icon}
                isChecked={privileges[option.id]}
                onToggle={() => handleTogglePrivilege(option.id)}
              />
            ))}
          </div>
          <button
            className={clsx(
              "w-full text-white py-2 px-4 rounded-lg transition",
              isPrivilegesChanged && !updatePrivileges.isPending
                ? "bg-primaryColor hover:bg-primaryColor/90"
                : "bg-primaryColor/20 cursor-not-allowed",
            )}
            onClick={handleSubmit}
            disabled={!isPrivilegesChanged || updatePrivileges.isPending}
          >
            {updatePrivileges.isPending ? "Loading..." : "Submit"}
          </button>
        </div>
      }
      showBorder={false}
      confirmButton={{ show: false }}
      onDismiss={onCancel}
    />
  );
}
